import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  PageHeader,
  Row,
  Col,
  Button,
  Tag,
  Progress,
  Popconfirm,
  Empty,
  Dropdown,
  Menu,
  Spin,
} from "antd";
import {
  ProjectOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  AppstoreOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { handleApiError, handleApiSuccess, MyBreadcrumb } from "utils";
import { ticketProjectListService, ticketProjectDeleteService } from "services/tickets";
import { TicketAccessGate, useTicketAccess } from "../_useTicketAccess";
import "../_styles.css";

function ProjectsContent() {
  const history = useHistory();
  const { canManage } = useTicketAccess();
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [projects, setProjects] = useState([]);

  const load = () => {
    setLoading(true);
    ticketProjectListService()
      .then((res) => {
        setProjects(res.data.items || []);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        handleApiError(err);
      });
  };

  useEffect(load, []);

  const onDelete = (id) => {
    setDeletingId(id);
    ticketProjectDeleteService(id)
      .then((res) => {
        handleApiSuccess(res);
        load();
      })
      .catch(handleApiError)
      .finally(() => setDeletingId(null));
  };

  return (
    <div className="ticket-system">
      <MyBreadcrumb
        items={[
          { label: "Home", path: "/" },
          { label: "Tickets", path: "/tickets/dashboard" },
          { label: "Projects", path: "" },
        ]}
      />
      <PageHeader
        title="Projects"
        subTitle="Organize work into projects with Kanban boards"
        avatar={{ icon: <ProjectOutlined /> }}
        extra={
          canManage && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => history.push("/tickets/projects/create")}
            >
              New Project
            </Button>
          )
        }
      />

      {!loading && projects.length === 0 ? (
        <Empty description="No projects yet" style={{ marginTop: 48 }}>
          {canManage && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => history.push("/tickets/projects/create")}>
              Create your first project
            </Button>
          )}
        </Empty>
      ) : (
        <Spin spinning={loading} tip="Loading projects…">
          <Row gutter={[20, 20]}>
          {projects.map((project) => {
            const stats = project.stats || {};
            const pct = stats.total ? Math.round((stats.done / stats.total) * 100) : 0;
            const menu = (
              <Menu>
                <Menu.Item
                  key="board"
                  icon={<AppstoreOutlined />}
                  onClick={() => history.push("/tickets/projects/board/" + project._id)}
                >
                  Open Board
                </Menu.Item>
                {canManage && (
                  <>
                    <Menu.Item
                      key="edit"
                      icon={<EditOutlined />}
                      onClick={() => history.push("/tickets/projects/update/" + project._id)}
                    >
                      Edit
                    </Menu.Item>
                    <Menu.Item key="delete" danger icon={<DeleteOutlined />}>
                      <Popconfirm
                        title="Delete this project and all tickets?"
                        onConfirm={() => onDelete(project._id)}
                        okButtonProps={{ loading: deletingId === project._id }}
                      >
                        <span>Delete</span>
                      </Popconfirm>
                    </Menu.Item>
                  </>
                )}
              </Menu>
            );

            return (
              <Col xs={24} sm={12} lg={8} xl={6} key={project._id}>
                <div
                  className="project-card"
                  onClick={() => history.push("/tickets/projects/board/" + project._id)}
                >
                  <div className="project-card-accent" style={{ background: project.color || "#6366f1" }} />
                  <div style={{ padding: 20 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>{project.name}</div>
                        <div style={{ color: "#64748b", fontSize: 13, marginTop: 4, minHeight: 40 }}>
                          {project.description || "No description"}
                        </div>
                      </div>
                      <Dropdown overlay={menu} trigger={["click"]} onClick={(e) => e.stopPropagation()}>
                        <Button type="text" size="small" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
                      </Dropdown>
                    </div>
                    <div style={{ marginTop: 16 }}>
                      <Progress percent={pct} size="small" strokeColor={project.color || "#6366f1"} />
                      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                        <Tag>{stats.open || 0} open</Tag>
                        <Tag color="green">{stats.done || 0} done</Tag>
                        {stats.overdue > 0 && <Tag color="red">{stats.overdue} overdue</Tag>}
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            );
          })}
          </Row>
        </Spin>
      )}
    </div>
  );
}

export default function TicketProjectsList() {
  return (
    <TicketAccessGate>
      <ProjectsContent />
    </TicketAccessGate>
  );
}
